import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Clock, Minus, ArrowUpRight, MapPin, Plus } from "lucide-react";
import { Eyebrow, Reveal } from "./Brand";
import { PACKAGES, brl0 } from "../../lib/data";
import { CorporateVideos } from "./CorporateVideos";

const PackageCard = ({ p, i }) => {
  const f = p.featured;
  return (
    <motion.article data-testid={`package-card-${p.id}`}
      initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -10 }}
      className={`relative flex flex-col rounded-[28px] p-8 lg:p-10 border ${f ? "bg-[#FFD200] text-[#08080C] border-[#FFD200] lg:-mt-8 shadow-[0_30px_80px_-20px_rgba(255,210,0,0.45)]" : "bg-[#111219] text-white border-white/10"}`}>
      {f && <span className="absolute -top-3 left-8 rounded-full bg-[#E8202A] text-white font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-1">Mais escolhido</span>}
      <div className="flex items-start justify-between">
        <span className={`font-mono text-xs uppercase tracking-[0.25em] ${f ? "text-[#08080C]/70" : "text-zinc-400"}`}>{p.label}</span>
        <span className={`font-display font-black text-5xl leading-none ${f ? "text-[#08080C]/15" : "text-white/10"}`}>{p.number}</span>
      </div>
      <h3 className="mt-4 font-display font-black text-2xl md:text-3xl uppercase">{p.name}</h3>
      <p className={`mt-2 text-sm ${f ? "text-[#08080C]/75" : "text-zinc-400"}`}>{p.tagline}</p>
      <p className="mt-8 font-display font-black text-4xl md:text-5xl tracking-tight" data-testid={`package-price-${p.id}`}>{brl0(p.price)}</p>
      <p className={`font-mono text-xs uppercase tracking-[0.2em] mt-2 ${f ? "text-[#08080C]/70" : "text-zinc-400"}`}>por evento · {p.hours} horas</p>
      <ul className="mt-8 space-y-3 flex-1">
        {p.features.map((x) => (
          <li key={x} className="flex items-start gap-3 text-sm md:text-base">
            <Check className={`w-5 h-5 shrink-0 mt-0.5 ${f ? "text-[#E8202A]" : "text-[#FFD200]"}`} />{x}
          </li>
        ))}
        {p.note && <li className="flex items-start gap-3 text-sm text-zinc-400"><Minus className="w-5 h-5 shrink-0 mt-0.5" />{p.note}</li>}
      </ul>
      <Link to={`/reservar?pacote=${p.id}`} data-testid={`package-select-btn-${p.id}`}
        className={`mt-10 group inline-flex items-center justify-between rounded-full pl-6 pr-2 py-2 font-semibold transition-colors ${f ? "bg-[#08080C] text-white hover:bg-[#E8202A]" : "bg-white/5 border border-white/15 hover:bg-[#FFD200] hover:text-[#08080C]"}`}>
        Reservar este pacote
        <span className={`w-10 h-10 rounded-full grid place-items-center transition-transform duration-300 group-hover:rotate-45 ${f ? "bg-[#FFD200] text-[#08080C]" : "bg-[#FFD200] text-[#08080C]"}`}><ArrowUpRight className="w-5 h-5" /></span>
      </Link>
    </motion.article>
  );
};

export const Packages = () => (
  <section id="pacotes" data-testid="packages-section" className="relative py-28 lg:py-40 bg-[#0B0B11] border-y border-white/5">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <Reveal className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-20">
        <div>
          <Eyebrow>Pacotes & valores</Eyebrow>
          <h2 className="mt-6 font-display font-black uppercase text-white text-4xl md:text-6xl leading-[0.95]">Escolha<br />sua <span className="text-red-grad">jogada.</span></h2>
        </div>
        <div className="max-w-md">
          <p className="text-zinc-300 text-base md:text-lg">Os pacotes de pelúcia incluem 4 horas de locação, jogadas ilimitadas, translado, montagem e monitor.</p>
          <p data-testid="extra-hour-info" className="mt-4 inline-flex items-start gap-2 rounded-2xl border border-[#FFD200]/40 px-4 py-2.5 text-sm text-zinc-200">
            <Clock className="w-4 h-4 text-[#FFD200] shrink-0 mt-0.5" /> <span>Precisa de mais tempo? Hora extra: <b className="text-[#FFD200] whitespace-nowrap">R$ 200,00 por hora</b></span>
          </p>
        </div>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
        {PACKAGES.filter((p) => p.line === "pelucias").map((p, i) => <PackageCard key={p.id} p={p} i={i} />)}
      </div>
      <Reveal className="mt-24 mb-10 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <Eyebrow>Opcional · eventos corporativos</Eyebrow>
          <h3 className="mt-5 font-display font-black uppercase text-white text-3xl md:text-5xl leading-[0.95]">Máquina com <span className="text-gold-grad">cápsulas.</span></h3>
        </div>
        <p className="text-zinc-300 max-w-md text-base md:text-lg">Indicada para eventos corporativos: escolha 4 horas com monitor ou a diária de 12 horas com suporte virtual.</p>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-6 lg:gap-8 items-stretch" data-testid="capsule-packages">
        {PACKAGES.filter((p) => p.line === "capsulas").map((p, i) => <PackageCard key={p.id} p={p} i={i} />)}
      </div>
      <Reveal className="mt-14 grid md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-white/10 p-6 flex items-center gap-4" data-testid="extra-plush-info">
          <span className="w-12 h-12 rounded-full bg-[#E8202A] grid place-items-center text-white shrink-0"><Plus className="w-5 h-5" /></span>
          <p className="text-zinc-300 text-sm"><b className="text-white font-display text-lg block">R$ 15 / pelúcia extra</b>Adicione quantas quiser na reserva.</p>
        </div>
        <div className="rounded-2xl border border-white/10 p-6">
          <p className="font-display text-white text-lg font-bold">Pagamento on-line</p>
          <p className="text-zinc-300 text-sm mt-1">Total com <b className="text-[#FFD200]">5% de desconto</b> ou sinal de 30% e o restante até 10 dias antes do evento.</p>
        </div>
        <div className="rounded-2xl border border-white/10 p-6 flex items-start gap-4">
          <MapPin className="w-5 h-5 text-[#FFD200] shrink-0 mt-1" />
          <p className="text-zinc-300 text-sm">Valores para a <b className="text-white">Grande Vitória</b>. Atendemos todo o ES — demais regiões, consulte o deslocamento.</p>
        </div>
      </Reveal>
      <CorporateVideos />
    </div>
  </section>
);
